import { useEffect } from "react";
import { Phone, Mail } from "lucide-react";
import { Img, Reveal } from "@/components/site/Motion";
import { SectionAction, ExtLabel } from "@/components/site/Section";

export const useSeo = (title, description) => {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", description);
    const og = document.querySelector('meta[property="og:title"]');
    if (og) og.setAttribute("content", title);
  }, [title, description]);
};

const StripImage = ({ it, i }) => (
  <li className="min-w-0" data-testid={`intro-strip-img-${i}`}>
    <figure>
      <div className="strip-frame img-frame">
        <Img name={it.name} alt={it.alt} w={it.w} h={it.h} sizes="(min-width: 768px) 33vw, 34vw" priority className="h-full w-full object-cover" style={{ objectPosition: it.pos || "50% 50%" }} />
      </div>
      <figcaption className="mt-2 text-[12px] leading-snug text-[#E3EAF0] md:text-sm">
        <span className="font-semibold text-white">{it.kind === "verified" ? it.setting : it.system}</span>
        <span className="block text-[#C9D6E2] md:inline"><span className="hidden md:inline"> · </span>{{ verified: "Alumicom work", project: "Alumicom project" }[it.kind] || "System example"}</span>
      </figcaption>
    </figure>
  </li>
);

export const PageIntro = ({ id, label, title, lead, images, action }) => (
  <section className="intro-top relative" aria-labelledby={`${id}-title`} data-testid={`${id}-intro`}>
    <div className="container-x">
      <ul className="intro-strip grid grid-cols-3 gap-[7px] md:gap-4" aria-label={`${label} photographs`}>
        {images.map((it, i) => <StripImage key={it.name} it={it} i={i} />)}
      </ul>
      <div className="intro-label mt-5"><ExtLabel>{label}</ExtLabel></div>
    </div>
    <div className="container-x mt-3">
      <div className="panel intro-panel" data-testid={`${id}-intro-panel`}>
        <Reveal className="max-w-3xl">
          <h1 id={`${id}-title`} className="text-[2rem] font-bold leading-[1.1] text-[#102A43] sm:text-5xl lg:text-[3.2rem]">{title}</h1>
          <p className="mt-4 text-base leading-relaxed text-[#151A20] md:text-lg">{lead}</p>
          <SectionAction section={`${id}-intro`} className="mt-6" {...action} />
        </Reveal>
      </div>
    </div>
  </section>
);

export const Steps = ({ steps }) => (
  <ol className="grid gap-px overflow-hidden rounded-[14px] border border-[#D9E1E8] bg-[#D9E1E8] sm:grid-cols-2 lg:grid-cols-3">
    {steps.map(([title, text], i) => (
      <li key={title} className="bg-white p-6 md:p-8">
        <p className="text-sm font-semibold tabular-nums text-[#7890A5]">{String(i + 1).padStart(2, "0")}</p>
        <h3 className="mt-3 text-xl font-semibold text-[#102A43]">{title}</h3>
        <p className="mt-2 text-base leading-relaxed text-[#151A20]">{text}</p>
      </li>
    ))}
  </ol>
);

export const ContactCTA = ({ id = "contact", title, lead, action }) => (
  <section id={id} className="section-gap relative pb-20 md:pb-28" aria-labelledby={`${id}-title`} data-testid={`section-${id}`}>
    <div className="container-x">
      <div className="panel-align">
        <ExtLabel>Start a conversation</ExtLabel>
        <Reveal className="ext-head mt-4 max-w-3xl">
          <h2 id={`${id}-title`} className="mt-3 text-3xl font-bold leading-[1.12] text-white md:text-4xl lg:text-5xl">{title}</h2>
          <p className="mt-4 text-base leading-relaxed text-[#E3EAF0] md:text-lg">{lead}</p>
        </Reveal>
        <div className="mt-8 grid max-w-3xl gap-3 text-white sm:grid-cols-2 sm:gap-4">
          <a href="tel:+19057618554" className="contact-tile" data-testid="cta-phone-link">
            <Phone aria-hidden="true" size={18} /><span><span className="block text-sm text-[#C9D6E2]">Call</span>905-761-8554 · Toll free 1-855-761-8554</span>
          </a>
          <a href="mailto:info@alumicom.ca" className="contact-tile" data-testid="cta-email-link">
            <Mail aria-hidden="true" size={18} /><span><span className="block text-sm text-[#C9D6E2]">Email</span>info@alumicom.ca</span>
          </a>
        </div>
        <SectionAction section={id} dark className="mt-10" {...action} />
      </div>
    </div>
  </section>
);
